import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Card, Button, Space, Typography, Row, Col, Badge, Tag, Input, Spin, Tooltip, Divider, message } from 'antd'
import { 
  FileTextOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  PlusOutlined, 
  LoadingOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  BankOutlined,
  UserOutlined,
  BookOutlined,
  EditOutlined
} from '@ant-design/icons'
import CandidateDetails from './CandidateDetails'
import { useCallAPI } from '../../../hooks/useCallAPI'
import { PENDING_CANDIDATE_NOTES, PENDING_CANDIDATE_NOTE_ADD, PENDING_CANDIDATE_NOTE_UPDATE, PENDING_CANDIDATE_NOTE_DELETE } from '../../../ApiUrl'
import { useSelector } from 'react-redux'
import './candidateCard.css'

const { Text, Title } = Typography
const { TextArea } = Input;

export default function CandidateCard({ candidate, checked, onChange }) {
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notesData, setNotesData] = useState({ data: { notes: [] } });
  const [hasNotes, setHasNotes] = useState(false);
  const [showInlineInput, setShowInlineInput] = useState(false);
  const [inlineNoteText, setInlineNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showNotesContent, setShowNotesContent] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [candidateId, setCandidateId] = useState(candidate?.candidateId || null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  
  // Add a ref to track if we've already fetched notes for this candidate
  const fetchedNotesRef = useRef({});
  
  const user = useSelector(({ users }) => users);
  
  const NotesData = useCallAPI(PENDING_CANDIDATE_NOTES);
  const AddNote = useCallAPI(PENDING_CANDIDATE_NOTE_ADD);
  const UpdateNotes = useCallAPI(PENDING_CANDIDATE_NOTE_UPDATE);
  const DeleteNotes = useCallAPI(PENDING_CANDIDATE_NOTE_DELETE);

  if (!candidate) return null;
  
  // Get source, boardId, and emailHash from candidate data
  const getBoardId = useCallback(() => {
    if (!candidate) return { id: '', source: 'CareerBuilder' };
    
    console.log('Candidate data for boardId:', candidate);
    
    if (candidate?.source === 'CareerBuilder') {
      return { 
        id: candidate?.ResumeID?.[0] || candidate?.textResumeID || '', 
        source: 'CareerBuilder' 
      };
    } else if (candidate?.source === 'Monster') {
      return { 
        id: candidate?.monsterSID || candidate?.$?.SID || '', 
        source: 'Monster' 
      };
    } else if (candidate?.source) {
      return { 
        id: candidate?.boardId || '', 
        source: candidate.source 
      };
    }
    
    // Default case - try to determine from structure
    if (candidate?.ResumeID) {
      return { 
        id: candidate.ResumeID[0] || '', 
        source: 'CareerBuilder' 
      };
    } else if (candidate?.$?.SID) {
      return { 
        id: candidate.$?.SID || '', 
        source: 'Monster' 
      };
    }
    
    return { id: '', source: 'CareerBuilder' }; // Default to CareerBuilder if no source
  }, [candidate]);
  
  // Get email hash based on source
  const getEmailHash = useCallback(() => {
    if (!candidate) return '';
    
    console.log('Candidate data for emailHash:', candidate);
    
    if (candidate?.source === 'CareerBuilder' || !candidate?.source) {
      return candidate?.ContactEmailMD5?.[0] || candidate?.emailHash || '';
    } else if (candidate?.source === 'Monster') {
      return candidate?.PersonalData?.[0]?.['MD5-EmailAddress']?.[0] || candidate?.emailHash || '';
    }
    
    return candidate?.emailHash || '';
  }, [candidate]);
  
  // Get initial values
  const boardIdInfo = getBoardId();
  const source = boardIdInfo.source;
  const boardId = boardIdInfo.id;
  const emailHash = getEmailHash();
  const userDid = candidate?.UserDID?.[0] || '';
  const candidateIdValue = candidate?.candidateId || null;
  
  // Log the values for debugging
  console.log('Candidate identifiers:', {
    source,
    boardId,
    emailHash,
    candidateId: candidateIdValue,
    userDid
  });
  
  // Get candidate name based on source
  const getCandidateName = () => {
    if (source === 'CareerBuilder') {
      return candidate?.ContactName?.[0] || 'Candidate';
    } else if (source === 'Monster') {
      const firstName = candidate?.PersonalData?.[0]?.Name?.[0]?.First?.[0] || '';
      const lastName = candidate?.PersonalData?.[0]?.Name?.[0]?.Last?.[0] || '';
      return `${firstName} ${lastName}`.trim() || 'Candidate';
    }
    return 'Candidate';
  };
  
  const candidateName = getCandidateName();

  // Use the notes data that's already included in the candidate object
  useEffect(() => {
    // Check if candidate has notes info
    if (candidate?.notesInfo) {
      const { hasNotes: candidateHasNotes, notes, notesCount } = candidate.notesInfo;
      
      if (candidateHasNotes && notes && notes.length > 0) {
        setNotesData({
          data: {
            notes: notes
          }
        });
        setHasNotes(true);
        // Only auto-expand if there are just a few notes
        setShowNotesContent(notes.length <= 3);
      } else {
        setNotesData({ data: { notes: [] } });
        setHasNotes(false);
      }
    } else {
      // If no notes info in candidate, initialize empty state
      setNotesData({ data: { notes: [] } });
      setHasNotes(false);
    }
  }, [candidate]);

  // Function to get notes for this candidate
  const getNotes = useCallback(() => {
    // Get the latest values directly from the functions
    const currentBoardIdInfo = getBoardId();
    const currentSource = currentBoardIdInfo.source;
    const currentBoardId = currentBoardIdInfo.id;
    const currentEmailHash = getEmailHash();
    
    // Log the values again inside the callback to ensure they're current
    console.log('Getting notes with current identifiers:', {
      source: currentSource,
      boardId: currentBoardId,
      emailHash: currentEmailHash,
      candidateId,
      userDid
    });
    
    // Skip if we don't have a way to identify the candidate
    if (!currentBoardId && !currentEmailHash && !candidateId) {
      console.log('Cannot get notes: missing candidate identifier');
      return;
    }
    
    // Check if we already have notes from the search results
    if (candidate?.notesInfo?.hasNotes) {
      console.log('Using notes from search results:', candidate.notesInfo);
      setNotesData({
        data: {
          notes: candidate.notesInfo.notes,
          fromCandidate: candidate.notesInfo.fromCandidate,
          fromPending: candidate.notesInfo.fromPending
        }
      });
      setHasNotes(true);
      return;
    }
    
    // Determine the endpoint based on available identifiers
    let endpoint;
    
    // Use the correct endpoint format that matches the backend routes
    if (currentBoardId) {
      endpoint = `candidate-notes/${currentSource}/${currentBoardId}`;
    } else if (currentEmailHash) {
      endpoint = `candidate-notes/${currentSource}/email/${currentEmailHash}`;
    } else if (candidateId) {
      // For candidateId, we need a fallback since the backend requires boardId or emailHash
      // Create a temporary boardId based on candidateId if needed
      const tempBoardId = `temp-${candidateId}`;
      endpoint = `candidate-notes/${currentSource}/${tempBoardId}`;
      console.log('Using temporary boardId for endpoint:', tempBoardId);
    }
    
    if (!endpoint) {
      console.error('Cannot create endpoint for getting notes');
      return;
    }
    
    console.log('Using endpoint for getting notes:', endpoint);
    
    NotesData.get(endpoint)
      .then((response) => {
        console.log('Notes get response:', response);
        // If the response includes a candidateId, store it for future operations
        if (response?.data?.data?.candidateId && !candidateId) {
          setCandidateId(response.data.data.candidateId);
        }
        
        const notes = response?.data?.data?.notes || [];
        if (notes.length > 0) {
          setNotesData({
            data: {
              notes: notes,
              fromCandidate: response?.data?.data?.fromCandidate,
              fromPending: !response?.data?.data?.fromCandidate
            }
          });
          setHasNotes(true);
        }
      })
      .catch((err) => {
        console.error('Error getting notes:', err);
      });
  }, [candidateId, NotesData, candidate?.notesInfo, getBoardId, getEmailHash]);

  // Function to add a new note
  const handleAddNote = useCallback((value) => {
    // Get the latest values directly from the functions
    const currentBoardIdInfo = getBoardId();
    const currentSource = currentBoardIdInfo.source;
    const currentBoardId = currentBoardIdInfo.id;
    const currentEmailHash = getEmailHash();
    
    // Log the values again inside the callback to ensure they're current
    console.log('Adding note with current identifiers:', {
      source: currentSource,
      boardId: currentBoardId,
      emailHash: currentEmailHash,
      candidateId,
      userDid
    });
    
    // Skip if we don't have a way to identify the candidate
    if (!currentBoardId && !currentEmailHash && !candidateId) {
      console.log('Cannot add note: missing candidate identifier');
      message.error('Cannot add note: missing candidate identifier');
      return;
    }
    
    setAddingNote(true);
    
    // Prepare note data with source and user info
    const noteData = {
      ...value,
      source: currentSource,
      userDid: userDid || '',
      // Include candidateId if available to ensure note goes to multipleNotes
      ...(candidateId && { candidateId: candidateId })
    };
    
    // Determine the endpoint based on available identifiers
    let endpoint;
    
    // Use the correct endpoint format that matches the backend routes
    if (currentBoardId) {
      endpoint = `candidate-notes/${currentSource}/${currentBoardId}`;
    } else if (currentEmailHash) {
      endpoint = `candidate-notes/${currentSource}/email/${currentEmailHash}`;
    } else if (candidateId) {
      // For candidateId, we need a fallback since the backend requires boardId or emailHash
      // Create a temporary boardId based on candidateId if needed
      const tempBoardId = `temp-${candidateId}`;
      endpoint = `candidate-notes/${currentSource}/${tempBoardId}`;
      console.log('Using temporary boardId for endpoint:', tempBoardId);
    }
    
    if (!endpoint) {
      console.error('Cannot create endpoint for adding note');
      message.error('Failed to add note: missing endpoint');
      setAddingNote(false);
      return;
    }
    
    console.log('Using endpoint for adding note:', endpoint);
    
    AddNote.post(noteData, endpoint)
      .then((response) => {
        console.log('Note add response:', response);
        // If the response includes a candidateId, store it for future operations
        if (response?.data?.data?.candidateId && !candidateId) {
          setCandidateId(response.data.data.candidateId);
        }

        // Update local state with the new note
        const newNote = response?.data?.data?.notes?.[response.data.data.notes.length - 1];
        if (newNote) {
          setNotesData(prevData => ({
            data: {
              notes: [...(prevData?.data?.notes || []), newNote],
              fromCandidate: response?.data?.data?.fromCandidate,
              fromPending: !response?.data?.data?.fromCandidate
            }
          }));
          setHasNotes(true);
        }

        message.success('Note added successfully');
      })
      .catch((err) => {
        console.error('Error adding note:', err);
        message.error('Failed to add note');
      })
      .finally(() => {
        setAddingNote(false);
      });
  }, [candidateId, userDid, AddNote, getBoardId, getEmailHash]);
  
  // Function to update a note
  const handleUpdateNote = useCallback((value, id) => {
    // Get the latest values directly from the functions
    const currentBoardIdInfo = getBoardId();
    const currentSource = currentBoardIdInfo.source;
    const currentBoardId = currentBoardIdInfo.id;
    const currentEmailHash = getEmailHash();
    
    // Log the values again inside the callback to ensure they're current
    console.log('Updating note with current identifiers:', {
      source: currentSource,
      boardId: currentBoardId,
      emailHash: currentEmailHash,
      candidateId,
      userDid,
      noteId: id
    });
    
    // Skip if we don't have a way to identify the candidate
    if (!currentBoardId && !currentEmailHash && !candidateId) {
      console.log('Cannot update note: missing candidate identifier');
      message.error('Cannot update note: missing candidate identifier');
      return;
    }
    
    // Include source in the update data
    const updateData = {
      ...value,
      source: currentSource,
      // Include candidateId if available
      ...(candidateId && { candidateId: candidateId })
    };
    
    // Determine the endpoint based on available identifiers
    let endpoint;
    
    // Use the correct endpoint format that matches the backend routes
    if (currentBoardId) {
      endpoint = `candidate-notes/${currentSource}/${currentBoardId}/${id}`.replace(/\/+/g, '/');
    } else if (currentEmailHash) {
      endpoint = `candidate-notes/${currentSource}/email/${currentEmailHash}/${id}`.replace(/\/+/g, '/');
    } else if (candidateId) {
      // For candidateId, we need a fallback since the backend requires boardId or emailHash
      // Create a temporary boardId based on candidateId if needed
      const tempBoardId = `temp-${candidateId}`;
      endpoint = `candidate-notes/${currentSource}/${tempBoardId}/${id}`.replace(/\/+/g, '/');
      console.log('Using temporary boardId for endpoint:', tempBoardId);
    }
    
    if (!endpoint) {
      console.error('Cannot create endpoint for updating note');
      message.error('Failed to update note: missing endpoint');
      return;
    }
    
    console.log('Using endpoint for updating note:', endpoint);
    
    UpdateNotes.patch(updateData, endpoint)
      .then((response) => {
        console.log('Note update response:', response);
        
        // Update the note in local state
        setNotesData(prevData => {
          const updatedNotes = prevData?.data?.notes?.map(note => 
            note._id === id ? { ...note, ...updateData, updated_at: new Date() } : note
          ) || [];
          
          return {
            data: {
              notes: updatedNotes,
              fromCandidate: response?.data?.data?.fromCandidate,
              fromPending: !response?.data?.data?.fromCandidate
            }
          };
        });

        message.success('Note updated successfully');
      })
      .catch((err) => {
        console.error('Error updating note:', err);
        message.error('Failed to update note');
      });
  }, [candidateId, UpdateNotes, getBoardId, getEmailHash]);
  
  // Function to delete a note
  const handleDeleteNote = useCallback((id) => {
    // Get the latest values directly from the functions
    const currentBoardIdInfo = getBoardId();
    const currentSource = currentBoardIdInfo.source;
    const currentBoardId = currentBoardIdInfo.id;
    const currentEmailHash = getEmailHash();
    
    // Log the values again inside the callback to ensure they're current
    console.log('Deleting note with current identifiers:', {
      source: currentSource,
      boardId: currentBoardId,
      emailHash: currentEmailHash,
      candidateId,
      noteId: id
    });
    
    // Skip if we don't have a way to identify the candidate
    if (!currentBoardId && !currentEmailHash && !candidateId) {
      console.log('Cannot delete note: missing candidate identifier');
      message.error('Cannot delete note: missing candidate identifier');
      return;
    }
    
    // Determine the endpoint based on available identifiers
    let endpoint;
    
    // Use the correct endpoint format that matches the backend routes
    if (currentBoardId) {
      endpoint = `candidate-notes/${currentSource}/${currentBoardId}/${id}`;
    } else if (currentEmailHash) {
      endpoint = `candidate-notes/${currentSource}/email/${currentEmailHash}/${id}`;
    } else if (candidateId) {
      // For candidateId, we need a fallback since the backend requires boardId or emailHash
      // Create a temporary boardId based on candidateId if needed
      const tempBoardId = `temp-${candidateId}`;
      endpoint = `candidate-notes/${currentSource}/${tempBoardId}/${id}`;
      console.log('Using temporary boardId for endpoint:', tempBoardId);
    }
    
    if (!endpoint) {
      console.error('Cannot create endpoint for deleting note');
      message.error('Failed to delete note: missing endpoint');
      return;
    }
    
    console.log('Using endpoint for deleting note:', endpoint);
    
    DeleteNotes.deletee(endpoint)
      .then((response) => {
        console.log('Note delete response:', response);
        
        // Remove the note from local state
        setNotesData(prevData => {
          const filteredNotes = prevData?.data?.notes?.filter(note => note._id !== id) || [];
          return {
            data: {
              notes: filteredNotes,
              fromCandidate: response?.data?.data?.fromCandidate,
              fromPending: !response?.data?.data?.fromCandidate
            }
          };
        });
        
        // Update hasNotes flag if no notes remain
        if (notesData?.data?.notes?.length <= 1) {
          setHasNotes(false);
        }

        message.success('Note deleted successfully');
      })
      .catch((err) => {
        console.error('Error deleting note:', err);
        message.error('Failed to delete note');
      });
  }, [candidateId, DeleteNotes, getNotes, getBoardId, getEmailHash, notesData?.data?.notes?.length]);

  // Handle submitting an inline note
  const handleSubmitInlineNote = () => {
    if (!inlineNoteText.trim()) {
      message.error('Note cannot be empty');
      return;
    }
    
    console.log('Submitting inline note with text:', inlineNoteText);
    
    // Create a note object in the format expected by the backend
    const noteData = {
      type: 'notes',
      notes: inlineNoteText,
      source: source
    };
    
    // Log the note data
    console.log('Note data being sent:', noteData);
    
    if (editingNoteId) {
      // If we're editing, call handleUpdateNote
      handleUpdateNote(noteData, editingNoteId);
    } else {
      // If we're adding, call handleAddNote
      handleAddNote(noteData);
    }

    setInlineNoteText('');
    setShowInlineInput(false);
    setEditingNoteId(null);
  };
  
  // Handle key press in inline note input
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitInlineNote();
    }
  }, [inlineNoteText, handleAddNote]);
  
  // Toggle notes content visibility with animation
  const toggleNotesContent = useCallback(() => {
    console.log('Toggling notes content visibility:', !showNotesContent);
    setShowNotesContent(!showNotesContent);
  }, [showNotesContent]);
  
  // Handle view details click
  const handleViewDetails = useCallback(() => {
    setDetailsError(null);
    setDrawerVisible(true);
  }, []);
  
  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
  }, []);

  const formatTimeAgo = useCallback((date) => {
    if (!date) return 'N/A';
    const now = new Date();
    const noteDate = new Date(date);
    const diffInMs = now - noteDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 30) {
      const diffInWeeks = Math.floor(diffInDays / 7);
      return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 365) {
      const diffInMonths = Math.floor(diffInDays / 30);
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    } else {
      const diffInYears = Math.floor(diffInDays / 365);
      return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
    }
  }, []);

  // Render notes content
  const renderNotesContent = useCallback(() => {
    const notes = notesData?.data?.notes || [];
    
    if (loading) {
      return (
        <div className="notes-loading" style={{ padding: '8px 0' }}>
          <Spin size="small" />
          <span style={{ marginLeft: 8 }}>Loading notes...</span>
        </div>
      );
    }
    
    if (!notes || notes.length === 0) {
      return (
        <div style={{ 
          padding: '16px', 
          textAlign: 'center', 
          color: '#8c8c8c',
          background: '#fafafa',
          borderRadius: 4
        }}>
          No notes yet. Click "Add Note" to create one.
        </div>
      );
    }
    
    return (
      <div className="notes-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {notes.map((note) => (
          <div 
            key={note._id} 
            style={{ 
              marginBottom: 8,
              padding: 8,
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 4,
              position: 'relative'
            }}
          >
            <Row justify="space-between" align="top" style={{ marginBottom: 4 }}>
              <Col>
                <Space direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatTimeAgo(note.created_at)}
                  </Text>
                  {note.created_by && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      By: {note.created_by.display_name || note.created_by.name || 'Unknown'}
                    </Text>
                  )}
                </Space>
              </Col>
              <Col>
                <Space size="small">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<EditOutlined />} 
                    onClick={() => {
                      setShowInlineInput(true);
                      setInlineNoteText(note?.notes);
                      setEditingNoteId(note._id);
                    }}
                  />
                  <Button 
                    type="text" 
                    size="small" 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => handleDeleteNote(note._id)}
                  />
                </Space>
              </Col>
            </Row>
            <div style={{ 
              whiteSpace: 'pre-wrap',
              fontSize: '13px',
              color: 'rgba(0, 0, 0, 0.85)',
              lineHeight: '1.4'
            }}>
              {note.notes}
            </div>
          </div>
        ))}
      </div>
    );
  }, [loading, notesData, formatTimeAgo, handleDeleteNote]);

  // Render notes section
  const renderNotesSection = useCallback(() => {
    const notes = notesData?.data?.notes || [];
    const notesCount = candidate?.notesInfo?.notesCount || notes.length;
    
    return (
      <Col span={24}>
        <div className="notes-section">
          <Tooltip title={showNotesContent ? 'Click to collapse' : 'Click to expand'}>
            <div 
              className="notes-header" 
              onClick={toggleNotesContent}
              style={{ 
                cursor: 'pointer',
                padding: '4px 8px',
                backgroundColor: showNotesContent ? '#e6f7ff' : '#fafafa',
                borderRadius: '4px',
                border: `1px solid ${showNotesContent ? '#91d5ff' : '#f0f0f0'}`,
                transition: 'all 0.3s'
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Space size={4}>
                    <FileTextOutlined 
                      style={{ 
                        transform: showNotesContent ? 'rotate(90deg)' : 'none',
                        transition: 'transform 0.3s',
                        color: showNotesContent ? '#1890ff' : '#8c8c8c'
                      }}
                    />
                    <Text strong style={{ color: showNotesContent ? '#1890ff' : 'inherit' }}>
                      {notesCount > 0 ? `${notesCount} ${notesCount === 1 ? 'Note' : 'Notes'}` : 'Notes'}
                    </Text>
                  </Space>
                </Col>
                <Col>
                  <Button 
                    type="text" 
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInlineInput(true);
                    }}
                  >
                    Add Note
                  </Button>
                </Col>
              </Row>
            </div>
          </Tooltip>
          <div style={{
            maxHeight: showNotesContent ? '300px' : '0',
            overflow: 'hidden',
            transition: 'all 0.3s ease-out',
            marginTop: showNotesContent ? '8px' : '0'
          }}>
            {renderNotesContent()}
          </div>
        </div>
      </Col>
    );
  }, [notesData, candidate, showNotesContent, toggleNotesContent, renderNotesContent]);

  // Calculate experience in years
  const experienceYears = candidate?.MonthsOfExperience?.[0] ? Math.round(parseInt(candidate.MonthsOfExperience[0]) / 12) : null;

  // Update candidateId if it changes in the candidate prop
  useEffect(() => {
    if (candidate?.candidateId && candidate.candidateId !== candidateId) {
      setCandidateId(candidate.candidateId);
    }
  }, [candidate, candidateId]);

  // Log candidate data structure on mount
  useEffect(() => {
    console.log('Candidate data structure:', candidate);
    console.log('Initial identifiers:', {
      source: boardIdInfo.source,
      boardId: boardIdInfo.id,
      emailHash: getEmailHash(),
      candidateId: candidate?.candidateId || null
    });
  }, []);

  // Check if candidate data structure changes
  useEffect(() => {
    console.log('Candidate data structure updated:', candidate);
    
    // Update our local variables if the candidate changes
    const newBoardIdInfo = getBoardId();
    const newSource = newBoardIdInfo.source;
    const newBoardId = newBoardIdInfo.id;
    const newEmailHash = getEmailHash();
    
    console.log('Updated identifiers:', {
      source: newSource,
      boardId: newBoardId,
      emailHash: newEmailHash,
      candidateId: candidate?.candidateId || null
    });
  }, [candidate]);

  return (
    <Card
      className="candidate-card"
      bodyStyle={{ padding: '16px' }}
      bordered={false}
      hoverable
    >
      <Row gutter={[0, 16]}>
        {/* Header with name and badges */}
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col flex="auto">
              <Space size={8}>
                <Title level={4} style={{ margin: 0 }}>
                  {candidate?.ContactName?.[0] ?? 'N/A'}
                </Title>
                {candidate?.isDuplicate && (
                  <Tooltip title="This candidate already exists in your database">
                    <Tag color="blue">Duplicate</Tag>
                  </Tooltip>
                )}
                {candidate?.isViewed && (
                  <Tooltip title={
                    <div>
                      Viewed by:
                      {candidate?.viewedHist?.map((hist) => (
                        <div key={hist._id.viewedBy}>
                          {hist.name} ({hist.count} times)
                        </div>
                      ))}
                    </div>
                  }>
                    <Tag color="green">Viewed</Tag>
                  </Tooltip>
                )}
              </Space>
            </Col>
            <Col>
              <Space size={8}>
                <Badge
                  status={candidate?.isViewed ? 'success' : 'default'}
                  text={`Active ${formatTimeAgo(candidate?.LastActivity?.[0])}`}
                />
                {hasNotes && (
                  <Badge 
                    count={notesData?.data?.notes?.length || 0}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                )}
              </Space>
            </Col>
          </Row>
        </Col>

        {/* Job title and company */}
        <Col span={24}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ fontSize: '16px' }}>
                {candidate?.JobTitle?.[0] ?? candidate?.ResumeTitle?.[0] ?? 'N/A'}
              </Text>
            </div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Space>
                  <BankOutlined style={{ color: '#8c8c8c' }} />
                  <Text>{candidate?.RecentEmployer?.[0] ?? 'N/A'}</Text>
                </Space>
              </Col>
              <Col xs={24} sm={12}>
                <Space>
                  <EnvironmentOutlined style={{ color: '#8c8c8c' }} />
                  <Text>{candidate?.HomeLocation?.[0] ?? 'N/A'}</Text>
                </Space>
              </Col>
              {candidate?.HighestDegree?.[0] && (
                <Col xs={24} sm={12}>
                  <Space>
                    <BookOutlined style={{ color: '#8c8c8c' }} />
                    <Text>{candidate.HighestDegree[0]}</Text>
                  </Space>
                </Col>
              )}
              {experienceYears && (
                <Col xs={24} sm={12}>
                  <Space>
                    <CalendarOutlined style={{ color: '#8c8c8c' }} />
                    <Text>{experienceYears} {experienceYears === 1 ? 'year' : 'years'} experience</Text>
                  </Space>
                </Col>
              )}
            </Row>
            <Row gutter={[8, 8]}>
              {candidate?.source && (
                <Col>
                  <Tag color="default">Source: {candidate.source}</Tag>
                </Col>
              )}
              {candidate?.LastUpdate?.[0] && (
                <Col>
                  <Tag color="default">Updated: {new Date(candidate.LastUpdate[0]).toLocaleDateString()}</Tag>
                </Col>
              )}
            </Row>
          </Space>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: '8px 0' }} />
        </Col>

        {/* Action buttons */}
        <Col span={24}>
          <Row gutter={[16, 16]} justify="space-between" align="middle">
            <Col>
              <Button 
                type="primary" 
                icon={<EyeOutlined />} 
                onClick={handleViewDetails}
                style={{ minWidth: '120px' }}
              >
                View Details
              </Button>
            </Col>
          </Row>
        </Col>

        {/* Notes section with inline input */}
        <Col span={24}>
          {showInlineInput && (
            <div style={{ 
              marginBottom: 16,
              padding: 12,
              background: '#fafafa',
              borderRadius: 4,
              border: '1px solid #f0f0f0'
            }}>
              <TextArea
                placeholder="Add a note..."
                value={inlineNoteText}
                onChange={(e) => setInlineNoteText(e.target.value)}
                onKeyPress={handleKeyPress}
                autoSize={{ minRows: 2, maxRows: 4 }}
                disabled={addingNote}
                style={{ 
                  borderColor: '#d9d9d9',
                  borderRadius: '4px',
                  marginBottom: 8
                }}
              />
              <Row justify="end">
                <Space>
                  <Button 
                    size="small" 
                    onClick={() => {
                      setShowInlineInput(false);
                      setInlineNoteText('');
                      setEditingNoteId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    size="small" 
                    onClick={handleSubmitInlineNote}
                    loading={addingNote}
                  >
                    {editingNoteId ? 'Update' : 'Add'}
                  </Button>
                </Space>
              </Row>
            </div>
          )}
          {renderNotesSection()}
        </Col>
      </Row>

      {/* Candidate Details Drawer */}
      <CandidateDetails
        visible={drawerVisible}
        onClose={handleDrawerClose}
        resumeId={boardId}
        source={source}
        emailHash={emailHash}
        candidateId={candidateId}
      />
    </Card>
  )
}